import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import Card from 'components/Card';
import CardContent from 'components/CardContent';
import HorizontalScroller from 'components/HorizontalScroller';
import SectionTitle from 'components/SectionTitle';
import { isMobile } from 'helpers';
import { seeMoreProps } from 'services/urlService';
import CollectionEntryViewDialog from './CollectionEntryViewDialog';
import PreviewEntryCard from './PreviewEntryCard';
import useEntryViewDialog from './useEntryViewDialog';
import usePageCollections from './usePageCollections';

const emptyArray = [];

const Slide = styled('div')`
  flex: 0 0 ${isMobile ? '180px' : '220px'};
`;

const CollectionWidget = ({ title, entries = emptyArray, pageId, seeMoreUrl, ...rest }) => {

  const { openEntry, dialogProps } = useEntryViewDialog(entries);

  // Cards carry no content, so the opened entry comes from its page's collections. They
  // load on the first open, and again on the next one if that load brought nothing back.
  const [wasOpened, setWasOpened] = useState(false);
  const collections = usePageCollections(pageId, wasOpened);

  useEffect(() => {
    if (dialogProps.entry) {
      setWasOpened(true);
    } else if (collections.length === 0) {
      setWasOpened(false);
    }
  }, [dialogProps.entry, collections.length]);

  const { openedEntry, attributes } = useMemo(() => {
    const collection = collections.find((item) => item.id === dialogProps.entry?.collectionId);
    return {
      openedEntry: collection?.entries?.find((entry) => entry.id === dialogProps.entry?.id),
      attributes: collection?.attributes ?? [],
    };
  }, [collections, dialogProps.entry]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div {...rest}>
      <Card>
        <CardContent>
          <SectionTitle seeMore={seeMoreProps(seeMoreUrl)}>{title}</SectionTitle>
          <HorizontalScroller atCardBottom>
            {entries.map(entry => (
              <Slide key={entry.id}>
                <PreviewEntryCard entry={entry} onClick={() => openEntry(entry)} />
              </Slide>
            ))}
          </HorizontalScroller>
        </CardContent>
      </Card>
      <CollectionEntryViewDialog {...dialogProps} entry={openedEntry ?? dialogProps.entry} attributes={attributes} />
    </div>
  );
};

CollectionWidget.propTypes = {
  title: PropTypes.node,
  entries: PropTypes.array,
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  seeMoreUrl: PropTypes.string,
};

export default CollectionWidget;
